import type { FC, ReactElement } from "react"

import { AnimatePresence, LazyMotion, domAnimation, motion } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import { ErrorBoundary } from "react-error-boundary"
import {
    type NavigateFunction,
    type Params,
    type SetURLSearchParams,
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom"
import { useMount, useShallowCompareEffect } from "react-use"

import { ErrorHint } from "@/3514/components"
import { ECoachTab, EPath } from "@/3514/constants"
import { diagnosticSummaryEntityLoader } from "@/3514/entities/diagnostic-summary"
import {
    EWindowCustomEvent,
    type TScreenBreakpoints,
    type TUserState,
    type TWindowCustomEventDispatcher,
    useIsNetworkFast,
    useScreenBreakpoints,
    useUser,
    useWindowCustomEventDispatcher
} from "@/3514/hooks"
import { type TParticipantByIdQuery, useParticipantByIdQuery } from "@/3514/queries"
import { idleCb, isUserCoach, isUserManager, seconds } from "@/3514/utils"

import classes from "./coach.individual-participant.styles"
import DiagnosticTab from "./coach.individual-participant.tab.diagnostic"
import PlanTab from "./coach.individual-participant.tab.plan"
import SessionsTab from "./coach.individual-participant.tab.sessions"

const CoachIndividualParticipantPage: FC = (): ReactElement => {
    const navigate: NavigateFunction = useNavigate()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const { user }: TUserState = useUser()

    const isNetworkFast: boolean = useIsNetworkFast()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const { participantId: participantIdParam }: Readonly<Params> = useParams()

    const participantId: number = Number(participantIdParam)

    const tabKey: string = searchParams.get("tab")

    const isPlanTab: boolean = tabKey === ECoachTab.IndividualParticipant.Plan,
        isSessionsTab: boolean = tabKey === ECoachTab.IndividualParticipant.Sessions,
        isDiagnosticTab: boolean = tabKey === ECoachTab.IndividualParticipant.Diagnostic

    useMount((): void => {
        if (isEmpty(participantIdParam)) navigate(EPath.Coach_Dashboard({ tab: "Sessions" }), { replace: true })

        if (!isDiagnosticTab && isNetworkFast) idleCb((): void => void diagnosticSummaryEntityLoader(), seconds(30))
    })

    const {
        data: participant,
        isSuccess: isParticipantQuerySuccess,
        isFetching: isParticipantQueryLoading
    }: TParticipantByIdQuery = useParticipantByIdQuery({
        userId: participantId
    })

    const dispatchWindowCustomEvent: TWindowCustomEventDispatcher<EWindowCustomEvent.CoachIndividualParticipantMeta> =
        useWindowCustomEventDispatcher(EWindowCustomEvent.CoachIndividualParticipantMeta)

    useShallowCompareEffect((): void => {
        if (!isParticipantQuerySuccess) return

        function dispatcher(): void {
            dispatchWindowCustomEvent({
                firstName: participant.firstName,
                lastName: participant.lastName,
                photo: participant.photo,
                cohortName: participant.cohortName
            })
        }

        if (breakpoints.sm || breakpoints.xs) requestAnimationFrame((): void => dispatcher())
        else idleCb((): void => dispatcher(), seconds(10))
    }, [isParticipantQuerySuccess, dispatchWindowCustomEvent, participant])

    return (
        <ErrorBoundary
            resetKeys={[participantId, tabKey]}
            fallback={<ErrorHint isVisible containerClassName={classes.page.error()} />}
        >
            <LazyMotion features={domAnimation}>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={tabKey}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        style={{ display: "flex", flexDirection: "column" }}
                    >
                        {isPlanTab && (
                            <PlanTab
                                isParticipantLoading={isParticipantQueryLoading}
                                isPlanEnabled={Boolean(
                                    (isUserCoach(user) ? user.isCoachingPlanAvailable : isUserManager(user)) ||
                                        !isParticipantQuerySuccess ||
                                        (isParticipantQuerySuccess && !participant.isCoachingPlanAvailable)
                                )}
                            />
                        )}

                        {isDiagnosticTab && (
                            <DiagnosticTab
                                participantId={participantId}
                                isParticipantLoading={isParticipantQueryLoading}
                                isDiagnosticEnabled={
                                    isParticipantQuerySuccess
                                        ? participant.isDiagnosticPresent || participant.isReflectionPresent
                                        : false
                                }
                            />
                        )}

                        {isSessionsTab && <SessionsTab participantId={participantId} />}
                    </motion.div>
                </AnimatePresence>
            </LazyMotion>
        </ErrorBoundary>
    )
}

CoachIndividualParticipantPage.displayName = "CoachIndividualParticipantPage"

export { CoachIndividualParticipantPage }
